/**
 * Hotspot drill-down (`…&hotspot=<key>`): summary strip, failing human
 * tests in the judge's words, the area's problems by priority with the
 * step they were seen on, and where else they show up. Everything links
 * onward: the triage queue filtered to the area, the finding, the step.
 */
import { useState } from "react";
import type {
  HotspotBy,
  HotspotDetail,
  HotspotFilters,
  HotspotMember,
  HotspotMemberView,
  HotspotWindow,
} from "@iamu/shared";
import { Link } from "react-router-dom";
import { FindingRow } from "@/components/experience/finding-row";
import type { FindingType, Severity } from "@/components/experience/vocabulary";
import { PriorityChip } from "@/components/triage/priority-chip";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/chip";
import { Kicker } from "@/components/ui/kicker";
import { VoiceProse } from "@/components/ui/voice-prose";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Timestamp } from "@/components/ui/timestamp";
import { StepThumbnail } from "@/components/walkthrough/step-screenshot";
import { TargetCropThumbnail } from "@/components/walkthrough/zen-mode/target-crop-thumbnail";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { CheckCircle2, GitPullRequest, Info } from "lucide-react";
import {
  DirectionChip,
  FindingDeltaBadges,
  formatPressure,
  KEY_LABEL,
  OutcomeChip,
  plural,
  problemSummary,
  Sparkline,
  statusNote,
} from "./hotspot-vocabulary";
import { PressureExplanation } from "./pressure-formula";
import { queueAreaHref } from "./hotspot-url-state";
import { HotspotEpicDialog } from "./hotspot-epic-dialog";

type Detail = HotspotDetail & {
  memberViews: Record<string, HotspotMemberView>;
};

interface PanelProps {
  projectId: string;
  by: HotspotBy;
  hotspotKey: string;
  window: HotspotWindow;
  filters: HotspotFilters;
  includeSecondary: boolean;
  /** Link that re-keys the map (distribution tags). */
  pivotHref: (by: HotspotBy, key: string) => string;
  onClose: () => void;
}

export function HotspotPanel(props: PanelProps) {
  const { projectId, by, hotspotKey, onClose } = props;
  const { data, isLoading, error } = trpc.hotspot.get.useQuery({
    projectId,
    by,
    key: hotspotKey,
    window: props.window,
    filters: props.filters,
    includeSecondary: props.includeSecondary,
  });
  const title = data?.row.label ?? (isLoading ? "Loading hotspot" : "Hotspot");
  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto sm:max-w-2xl"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle className="text-lg font-semibold">{title}</SheetTitle>
          <SheetDescription>
            {KEY_LABEL[by]}
            {data?.row.context ? ` · ${data.row.context}` : ""}
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-8 p-4">
          {isLoading && <Skeleton className="h-40 w-full" />}
          {error && (
            <p className="text-sm text-foreground">
              This hotspot isn't in the current window or filters.
            </p>
          )}
          {data && <PanelBody detail={data} {...props} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function PanelBody(props: PanelProps & { detail: Detail }) {
  const { detail, projectId, by, pivotHref } = props;
  const { row, prior } = detail;
  const note = statusNote(row);
  const members = detail.members.pressure;
  const [epicOpen, setEpicOpen] = useState(false);
  const epicQuery = trpc.workflow.hotspotEpic.useQuery({
    projectId,
    hotspotKey: row.key,
  });
  return (
    <>
      <section
        aria-labelledby="hotspot-summary"
        className="rounded-lg border border-border/70 bg-card/60 p-4 space-y-4 shadow-xs"
      >
        <h3 id="hotspot-summary" className="sr-only">
          Summary
        </h3>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
          <div className="space-y-1">
            <dt className="text-xs font-medium text-muted-foreground">
              Pressure
            </dt>
            <dd className="flex items-baseline gap-2">
              <span className="text-2xl font-bold tabular-nums text-foreground">
                {formatPressure(row.pressure)}
              </span>
              {row.pressure != null && (
                <Chip
                  tone={
                    row.pressure >= 100
                      ? "critical"
                      : row.pressure >= 40
                        ? "important"
                        : "helps"
                  }
                >
                  {row.pressure >= 100
                    ? "High"
                    : row.pressure >= 40
                      ? "Moderate"
                      : "Low"}
                </Chip>
              )}
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-xs font-medium text-muted-foreground">
              Human tests
            </dt>
            <dd>
              <OutcomeChip outcome={row.outcome} />
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-xs font-medium text-muted-foreground">Trend</dt>
            <dd className="flex items-center gap-2">
              <Sparkline points={row.trend} />
              <DirectionChip direction={row.direction} />
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-xs font-medium text-muted-foreground">
              Walked
            </dt>
            <dd className="text-sm tabular-nums text-foreground font-medium">
              {plural(row.runs, "run")} · {plural(row.exposureSteps, "step")} ·{" "}
              {plural(row.personas, "persona")}
            </dd>
          </div>
        </dl>

        {/* Visual Pressure Exposure Bar */}
        {row.pressure != null && (
          <div className="space-y-1.5 pt-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Risk exposure meter</span>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-primary hover:underline underline-offset-4"
                  >
                    <Info className="h-3 w-3" />
                    How computed
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-4 text-xs" align="end">
                  <PressureExplanation row={row} prior={prior} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-3 gap-1 h-2 rounded-full overflow-hidden bg-muted/60 p-0.5">
              <div
                className={cn(
                  "h-full rounded-l-full transition-all",
                  row.pressure > 0 ? "bg-finding-helps/70" : "bg-muted",
                )}
              />
              <div
                className={cn(
                  "h-full transition-all",
                  row.pressure >= 40 ? "bg-severity-important/80" : "bg-muted",
                )}
              />
              <div
                className={cn(
                  "h-full rounded-r-full transition-all",
                  row.pressure >= 100 ? "bg-severity-critical/90" : "bg-muted",
                )}
              />
            </div>
          </div>
        )}

        <div className="border-t border-border/60 pt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <div className="flex flex-wrap items-center gap-2">
            <span>
              {problemSummary(row)} · {plural(row.strengths, "strength")}
              {note ? ` · ${note}` : ""}
            </span>
            <FindingDeltaBadges deltas={row.deltas} />
          </div>
          {row.trend.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-primary hover:underline underline-offset-4">
                Show trend data
              </summary>
              <table className="mt-2 w-full text-xs tabular-nums">
                <caption className="sr-only">
                  Priority per 10 steps and human-test pass rate per run
                </caption>
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1 pr-3 font-medium">Run started</th>
                    <th className="py-1 pr-3 text-right font-medium">Steps</th>
                    <th className="py-1 pr-3 text-right font-medium">
                      Per 10 steps
                    </th>
                    <th className="py-1 text-right font-medium">
                      Tests passed
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {row.trend.map((t) => (
                    <tr
                      key={t.walkthroughId}
                      className="border-t border-border/60"
                    >
                      <td className="py-1 pr-3">
                        <Link
                          to={`/projects/${projectId}/walkthroughs/${t.walkthroughId}`}
                          className="underline underline-offset-4"
                        >
                          <Timestamp date={t.startedAt} />
                        </Link>
                      </td>
                      <td className="py-1 pr-3 text-right">{t.steps}</td>
                      <td className="py-1 pr-3 text-right">
                        {t.value.toFixed(1)}
                      </td>
                      <td className="py-1 text-right">
                        {t.passRate == null
                          ? "—"
                          : `${Math.round(t.passRate * 100)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      </section>

      {detail.failingTests.length > 0 && (
        <section aria-labelledby="hotspot-tests" className="space-y-3">
          <Kicker as="h3">
            <span id="hotspot-tests">Failing human tests</span>
          </Kicker>
          <ul className="space-y-2.5">
            {detail.failingTests.map((t) => (
              <li
                key={`${t.outlineId}:${t.humanTestId}`}
                className="rounded-lg border border-border/70 bg-card/60 p-3 space-y-2 transition-colors hover:border-border"
              >
                <div className="flex items-start gap-2.5">
                  <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-destructive/15 text-destructive font-bold text-xs">
                    ?
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium leading-snug">
                      <VoiceProse>{t.question}</VoiceProse>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs pt-1.5 border-t border-border/40">
                  {t.scenarioName && (
                    <span className="text-muted-foreground">
                      Scenario:{" "}
                      <span className="text-foreground font-medium">
                        {t.scenarioName}
                      </span>
                    </span>
                  )}
                  <span className="text-border">·</span>
                  <ol
                    aria-label="Rating per run, oldest first"
                    className="flex flex-wrap items-center gap-1"
                  >
                    {t.ratings.map((r) => (
                      <li key={r.walkthroughId}>
                        <Chip
                          tone={
                            r.rating === "yes"
                              ? "helps"
                              : r.rating === "no"
                                ? "critical"
                                : "important"
                          }
                        >
                          {r.rating}
                        </Chip>
                      </li>
                    ))}
                  </ol>
                  <span className="text-muted-foreground">
                    · cites {plural(t.citedCanonicalIds.length, "finding")}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-labelledby="hotspot-problems" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-3">
          <Kicker as="h3">
            <span id="hotspot-problems">
              Problems adding pressure ({members.length})
            </span>
          </Kicker>
          {members.length > 0 && (
            <div className="flex items-center gap-2">
              {row.key.startsWith("route:") && (
                <Button asChild variant="outline" size="xs">
                  <Link
                    to={`/projects/${projectId}/findings?tab=pages&route=${encodeURIComponent(
                      row.key.replace(/^route:/, ""),
                    )}`}
                  >
                    View in Pages
                  </Link>
                </Button>
              )}
              <Button asChild variant="default" size="xs">
                <Link to={queueAreaHref(projectId, row.key)}>
                  <CheckCircle2 className="mr-1.5 h-3 w-3" />
                  Triage in the queue
                </Link>
              </Button>
              <Button
                variant="outline"
                size="xs"
                onClick={() => setEpicOpen(true)}
              >
                <GitPullRequest className="mr-1.5 h-3 w-3" />
                {epicQuery.data?.link
                  ? `Epic: ${epicQuery.data.link.displayKey ?? "linked"}`
                  : "Create epic issue…"}
              </Button>
            </div>
          )}
        </div>
        {members.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No open problems here in this window.
          </p>
        ) : (
          <ul className="space-y-2">
            {members.map((m) => (
              <MemberRow
                key={m.id}
                projectId={projectId}
                member={m}
                view={detail.memberViews?.[m.id]}
              />
            ))}
          </ul>
        )}
        {detail.strengths.length > 0 && (
          <p className="text-sm text-foreground">
            What works here:{" "}
            {detail.strengths.map((s, i) => (
              <span key={s.id}>
                {i > 0 && " · "}
                <Link
                  to={`/projects/${projectId}/findings/${s.id}`}
                  className="underline underline-offset-4"
                >
                  {s.title ?? "Untitled strength"}
                </Link>
              </span>
            ))}
          </p>
        )}
      </section>

      {Object.keys(detail.distribution).length > 0 && (
        <section aria-labelledby="hotspot-spread" className="space-y-3">
          <Kicker as="h3">
            <span id="hotspot-spread">These problems also show up in</span>
          </Kicker>
          <dl className="space-y-2 text-sm">
            {(
              Object.entries(detail.distribution) as Array<
                [
                  HotspotBy,
                  Array<{ key: string; label: string; count: number }>,
                ]
              >
            ).map(([k, items]) => (
              <div
                key={k}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-1"
              >
                <dt className="text-muted-foreground">{KEY_LABEL[k]}:</dt>
                {items.map((d) => (
                  <dd key={d.key}>
                    <Link
                      to={pivotHref(k, d.key)}
                      className="inline-flex min-h-6 items-center underline underline-offset-4"
                    >
                      {d.label} {d.count}
                    </Link>
                  </dd>
                ))}
              </div>
            ))}
          </dl>
        </section>
      )}
      {epicOpen && (
        <HotspotEpicDialog
          projectId={projectId}
          hotspotKey={row.key}
          by={by}
          label={row.label}
          open={epicOpen}
          onOpenChange={setEpicOpen}
        />
      )}
    </>
  );
}

function MemberRow({
  projectId,
  member,
  view,
}: {
  projectId: string;
  member: HotspotMember;
  view: HotspotMemberView | undefined;
}) {
  const evidence = view?.evidence;
  return (
    <li className="flex items-start gap-3 rounded-lg border border-border/70 bg-card/60 p-3 transition-colors hover:bg-muted/30 hover:border-border">
      <FindingRow
        variant="compact"
        headingLevel="h4"
        href={`/projects/${projectId}/findings/${member.id}`}
        finding={{
          id: member.id,
          title: member.title,
          description: view?.preview ?? "",
          type: (member.type ?? "hinders") as FindingType,
          severity: (member.severity ?? null) as Severity | null,
          state: member.state,
          stateReason: member.stateReason ?? null,
          observationCount: member.observationCount,
          personaName: view?.personaName ?? null,
        }}
        trailing={
          <div className="flex shrink-0 items-center gap-2">
            <PriorityChip
              canonicalId={member.id}
              priority={{
                priorityScore: member.priorityScore,
                priorityOverride: member.priorityOverride ?? null,
              }}
            />
            {evidence && (
              <Link
                to={`/projects/${projectId}/walkthroughs/${evidence.walkthroughId}?step=${evidence.stepNumber}`}
                aria-label={`Step ${evidence.stepNumber} evidence`}
                className="group/thumb relative block w-14 overflow-hidden rounded border border-border/80 bg-muted shadow-xs transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-ring"
              >
                {evidence.targetBox ? (
                  <TargetCropThumbnail
                    walkthroughId={evidence.walkthroughId}
                    stepNumber={evidence.stepNumber}
                    targetBox={evidence.targetBox}
                    targetTone={member.severity || member.type}
                    alt={`Step ${evidence.stepNumber} target preview`}
                    className="aspect-video w-full"
                  />
                ) : (
                  <StepThumbnail
                    walkthroughId={evidence.walkthroughId}
                    stepNumber={evidence.stepNumber}
                    alt={`Step ${evidence.stepNumber} evidence`}
                    className="aspect-video object-cover"
                  />
                )}
              </Link>
            )}
          </div>
        }
      />
    </li>
  );
}
